import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptor
const axiosInstance = axios.create();

// Add token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export { axiosInstance };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      // Clear invalid token
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      }, { withCredentials: true });
      
      // Store session token in localStorage for cross-origin requests
      localStorage.setItem('session_token', response.data.session_token);
      
      // Also set cookie for backwards compatibility
      document.cookie = `session_token=${response.data.session_token}; path=/; max-age=${7*24*60*60}; samesite=none; secure`;
      
      // Fetch full user data including founding_member status
      await checkAuth();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password, name, referral_code) => {
    try {
      const payload = { email, password, name };
      if (referral_code) payload.referral_code = referral_code;
      const response = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      
      // Store session token in localStorage for cross-origin requests
      localStorage.setItem('session_token', response.data.session_token);
      
      // Also set cookie for backwards compatibility
      document.cookie = `session_token=${response.data.session_token}; path=/; max-age=${7*24*60*60}; samesite=none; secure`;
      
      // Fetch full user data
      await checkAuth();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = async () => {
    try {
      await axiosInstance.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    setUser,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
